/**
 * Route Parameter Helpers
 *
 * Utilities for extracting and parsing route parameters with support for legacy formats.
 * Handles both new clean structure and old name~id format transparently.
 */

import { computed } from 'vue';
import { useRoute, type LocationQueryRaw, type RouteLocationNormalizedLoaded } from 'vue-router';

/**
 * Extract preview query params from a route
 * Use this to preserve preview mode across navigations
 */
export function getPreviewQuery(route: RouteLocationNormalizedLoaded): LocationQueryRaw {
   const query: LocationQueryRaw = {};
   if (route.query.preview) query.preview = route.query.preview;
   if (route.query.lang) query.lang = route.query.lang;
   return query;
}

/**
 * Extract journey-related parameters from current route
 * handling both preview mode (via query params) and regular mode.
 *
 * Supports legacy format: name~id gets extracted to just id
 */
export function getJourneyParams(route: RouteLocationNormalizedLoaded) {
   const rawJourneyId = route.params.journeyId?.toString();
   const journeyId = rawJourneyId?.includes('~') ? rawJourneyId.split('~').pop() : rawJourneyId;

   const rawReferral = route.params.referral?.toString();

   // Check for preview mode via query params (new structure)
   const previewKey = route.query.preview as string | undefined;
   const previewLanguage = route.query.lang as string | undefined;

   return {
      journeyId,
      referral: rawReferral,
      isPreviewMode: !!previewKey,
      previewKey,
      previewLanguage,
   };
}

/**
 * Extract submission-related parameters from current route
 */
export function getSubmissionParams(route: RouteLocationNormalizedLoaded) {
   const rawSubmissionId = route.params.submissionId?.toString();
   const submissionId = rawSubmissionId?.includes('~') ? rawSubmissionId.split('~').pop() : rawSubmissionId;

   return {
      submissionId,
   };
}

/**
 * Extract journey progress parameters from query string
 */
export function getJourneyProgress(route: RouteLocationNormalizedLoaded) {
   return {
      step: route.query.step ? parseInt(route.query.step as string, 10) : null,
      page: route.query.page ? parseInt(route.query.page as string, 10) : null,
      module: route.query.module ? parseInt(route.query.module as string, 10) : null,
   };
}

/**
 * Composable for reactive journey route params
 */
export function useJourneyRouteParams() {
   const route = useRoute();

   const journeyId = computed(() => {
      const rawJourneyId = route.params.journeyId?.toString();
      return rawJourneyId?.includes('~') ? rawJourneyId.split('~').pop() : rawJourneyId;
   });

   const referral = computed(() => route.params.referral?.toString());

   const isPreviewMode = computed(() => !!route.query.preview);

   const previewKey = computed(() => route.query.preview as string | undefined);

   const previewLanguage = computed(() => route.query.lang as string | undefined);

   const step = computed(() => (route.query.step ? parseInt(route.query.step as string, 10) : null));

   const page = computed(() => (route.query.page ? parseInt(route.query.page as string, 10) : null));

   const module = computed(() => (route.query.module ? parseInt(route.query.module as string, 10) : null));

   /**
    * Get preview query params for use in router navigation
    */
   const previewQuery = computed(() => getPreviewQuery(route));

   return {
      journeyId,
      referral,
      isPreviewMode,
      previewKey,
      previewLanguage,
      step,
      page,
      module,
      previewQuery,
   };
}

